import React from 'react';
import axios from 'axios';
import {
  filterOptions,
  filterLabels,
  ALL,
  SEARCH_PATH,
  INTERNAL_SEARCH_ENDPOINT,
  FUNNELBACK_AUTO_SUGGEST_ENDPOINT,
  COLLECTIONS,
} from './constants';
import { isBrowser } from '../../utils/is-browser';

export const buildSearchQuery = (params) => {
  let searchQuery =
    isBrowser && window.location.pathname.match(SEARCH_PATH) ? '' : SEARCH_PATH;
  let count = 0;
  Object.keys(params).forEach((key) => {
    const param = params[key];
    if (param) {
      const encodedParam = encodeURIComponent(
        param
          ?.toString()
          ?.replace(/\+/g, '') // for + sign in LGBTQ+ to work
          ?.replace(/[.*?^${}()|[\]\\]/g, '\\$&')
          ?.trim()
      );

      searchQuery = count
        ? searchQuery.concat(`&${key}=${encodedParam}`)
        : searchQuery.concat(`?${key}=${encodedParam}`);
      count += 1;
    }
  });
  return searchQuery;
};

export const fetchSearchResults = async (query) => {
  const response = await axios(`${INTERNAL_SEARCH_ENDPOINT}${query}`);
  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.status);
};

export const fetchAutoSuggestResults = async (query, section) => {
  /**  Funnelback params
   * - collection:          the colletion to search (shelter-england/sheler-scotland)
   *                        determined by the SHELTER_SITE .env var
   * - partial_query:       the search term to provide suggestions for
   * - f.Section|section:   which section facet to return (Advice/Legal/Policy library)
   */

  const params = {
    collection: COLLECTIONS[process.env.SHELTER_SITE],
    partial_query: query,
  };

  // this doesn't currently work. leaving here for potential future implementation
  if (section) {
    params['f.Section|section'] = section;
  }

  const request = await axios.get(FUNNELBACK_AUTO_SUGGEST_ENDPOINT, {
    params,
  });

  if (request.status === 200) {
    return [...new Set(request.data)]; // remove any duplicates
  }

  return [];
};

export const getFilterOptions = ({ sections, total }) =>
  filterOptions
    .map((filter) => {
      let count;
      if (filter === ALL) {
        count = total;
      } else {
        const section = sections?.find(({ label }) => label === filter);
        count = section?.count ? section.count : 0;
      }

      return {
        value: filter,
        label: filterLabels[filter],
        count,
        disabled: !(count > 0),
      };
    })
    .filter(Boolean);

const buildPage = ({ query, section, i }) => ({
  index: i,
  path: buildSearchQuery({
    query,
    section,
    page: i,
  }),
});

export const getPagination = ({
  currentPage: currentPageZeroBased,
  total,
  limit,
  query,
  currentSection,
  sectionTotal,
}) => {
  const distance = 4;
  const totalPages =
    currentSection === ALL
      ? Math.ceil(total / limit)
      : Math.ceil(sectionTotal / limit);
  const currentPage = currentPageZeroBased + 1;
  const section = currentSection === ALL ? null : currentSection;

  const pagination = {
    all: [buildPage({ query, section, i: currentPage })],
    total: totalPages,
    current: currentPage,
    next: [],
    previous: [],
  };

  for (
    let i = currentPage - 1;
    i > 0 && i <= totalPages && i >= currentPage - distance;
    i -= 1
  ) {
    pagination.previous.unshift(buildPage({ query, section, i }));
  }

  for (
    let i = currentPage + 1;
    i > 0 && i <= totalPages && i <= currentPage + distance;
    i += 1
  ) {
    pagination.next.push(buildPage({ query, section, i }));
  }

  return pagination;
};

export const parseResults = (results) => {
  if (!results) return [];

  const { section } = results;

  // if there are no results, return spelling suggestions
  if (!results.total) {
    return [
      null, // total
      null, // filters
      section,
      results.spellingSuggestion,
      null, // items
      null, // pagination
      null, // suggestions
      null, // best bet
    ];
  }

  const filters = getFilterOptions(results, section);

  const { spellingSuggestion, suggestions } = results;

  // strip the domain from urls so we can provide relative paths
  const items = results.items.map((item) => ({
    ...item,
    path: new URL(item.url).pathname,
  }));

  const currentSection = results.sections
    ? results.sections.find(({ label }) => label === section)
    : ALL;

  const pagination = getPagination({
    ...results,
    currentSection: currentSection ? currentSection.label : ALL,
    sectionTotal: currentSection ? currentSection.count : results.total,
  });

  const bestBet = results.bestBets?.[0]
    ? {
        ...results.bestBets?.[0],
        path: (() => {
          const url = new URL(
            results.bestBets?.[0].trackingUrl
          ).searchParams.get('url');
          return new URL(url).pathname;
        })(),
      }
    : null;

  // return the total for the current section if one is selected
  const total = currentSection?.count || results.total;

  return [
    total,
    filters,
    section,
    spellingSuggestion,
    items,
    pagination,
    suggestions,
    bestBet,
  ];
};

export const emboldenMatchingStrings = (para, string) => {
  if (!string) return para;

  return para
    .split(new RegExp(`(\\b${string}\\b)+`, 'gi'))
    .map((part, i) =>
      part?.toLowerCase() === string?.toLowerCase() ? (
        <strong key={i}>{part}</strong>
      ) : (
        part
      )
    );
};
